/**
 * Table di antd con colonne ridimensionabili (AUD-F2.19): maniglia sul bordo destro dell'intestazione (mouse, penna,
 * tocco), doppio clic per tornare alla larghezza predefinita. Le larghezze restano nel browser dell'utente
 * (localStorage, una chiave per pagina e tabella). Stessa API di Table: si usa al suo posto.
 */
import { useCallback, useMemo, useRef, useState, type ThHTMLAttributes } from "react";
import { Table } from "antd";
import type { ColumnType, ColumnsType, TableProps } from "antd/es/table";
import { useLocation } from "react-router-dom";

const MIN_W = 56;
const PREFIX = "ap.colw.";

type Widths = Record<string, number>;
const load = (k: string): Widths => {
  try {
    return JSON.parse(localStorage.getItem(PREFIX + k) ?? "{}") as Widths;
  } catch {
    return {};
  }
};
const save = (k: string, w: Widths) => {
  try {
    if (Object.keys(w).length) localStorage.setItem(PREFIX + k, JSON.stringify(w));
    else localStorage.removeItem(PREFIX + k);
  } catch {
    /* storage non disponibile: le larghezze valgono solo per la sessione */
  }
};

type HeaderProps = ThHTMLAttributes<HTMLTableCellElement> & { colKey?: string; onResizeCol?: (key: string, w: number | null, done: boolean) => void };

function ResizableTh({ colKey, onResizeCol, children, style, ...rest }: HeaderProps) {
  const th = useRef<HTMLTableCellElement>(null);
  if (!colKey || !onResizeCol) return <th {...rest} style={style}>{children}</th>;
  const start = (e: React.PointerEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const x0 = e.clientX;
    const w0 = th.current?.getBoundingClientRect().width ?? 120;
    let last = w0;
    const move = (ev: PointerEvent) => {
      last = Math.max(MIN_W, Math.round(w0 + ev.clientX - x0));
      onResizeCol(colKey, last, false);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      onResizeCol(colKey, last, true);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  return (
    <th {...rest} ref={th} style={{ ...style, position: "relative" }}>
      {children}
      <span
        role="separator"
        aria-orientation="vertical"
        className="ap-col-resize"
        onPointerDown={start}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          e.stopPropagation();
          onResizeCol(colKey, null, true);
        }}
      />
    </th>
  );
}

const colKeyOf = <T,>(c: ColumnType<T>, i: number): string =>
  String(c.key ?? (Array.isArray(c.dataIndex) ? c.dataIndex.join(".") : c.dataIndex) ?? (typeof c.title === "string" ? c.title : `c${i}`));

export function ResizableTable<T extends object>({ columns, components, resizeId, ...props }: TableProps<T> & { resizeId?: string }) {
  const { pathname } = useLocation();
  const cols = (columns ?? []) as ColumnsType<T>;
  // chiave: pagina (id normalizzati) + firma delle colonne, così più tabelle nella stessa pagina non si mescolano
  const storeKey = useMemo(() => {
    const page = pathname.split("/").map((s) => (/\d/.test(s) || s.length > 20 ? ":id" : s)).join("/");
    return resizeId ?? `${page}|${cols.map((c, i) => colKeyOf(c as ColumnType<T>, i)).join(",")}`;
  }, [pathname, resizeId, cols]);
  const [widths, setWidths] = useState<Widths>(() => load(storeKey));
  const [loadedKey, setLoadedKey] = useState(storeKey);
  if (loadedKey !== storeKey) {
    setLoadedKey(storeKey);
    setWidths(load(storeKey));
  }

  const onResizeCol = useCallback(
    (key: string, w: number | null, done: boolean) => {
      setWidths((prev) => {
        const next = { ...prev };
        if (w == null) delete next[key];
        else next[key] = w;
        if (done) save(storeKey, next);
        return next;
      });
    },
    [storeKey],
  );

  const sized = useMemo(
    () =>
      cols.map((c, i) => {
        if ("children" in c && c.children) return c;
        const key = colKeyOf(c as ColumnType<T>, i);
        const width = widths[key] ?? (c as ColumnType<T>).width;
        const prevHeader = (c as ColumnType<T>).onHeaderCell;
        return {
          ...c,
          width,
          ...(widths[key] ? { ellipsis: true } : {}),
          onHeaderCell: (col: Parameters<NonNullable<ColumnType<T>["onHeaderCell"]>>[0]) => ({ ...(prevHeader?.(col) ?? {}), colKey: key, onResizeCol }) as React.HTMLAttributes<HTMLElement>,
        };
      }) as ColumnsType<T>,
    [cols, widths, onResizeCol],
  );

  return <Table<T> {...props} columns={sized} components={{ ...components, header: { ...components?.header, cell: ResizableTh } }} />;
}
